import './instrument';

import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';

import { AppProvider } from '@/providers/app-proivder';

import { Login } from '@/features/login';
import { Logout } from '@/features/logout';
import { Layout } from '@/features/layout';
import { ComingSoon } from '@/features/coming-soon';
import { AgreementPage } from '@/features/agreements';
import { Archive } from '@/features/archive/archive';
import { NoConnection } from '@/features/no-connection';
import { TagWorkspace } from '@/components/tag-workspace';
import { Personalization } from '@/features/personalization';
import { SubscriptionBilling } from '@/features/billing';
import { ProtectedRoutes } from '@/features/protected-routes';
import { CortexWorkspace } from '@/components/cortex-workspace';
import { DocumentWorkspace } from '@/components/document-workspace';
import { AccessDenied } from '@flintmint/web-shared/components/access-denied';
import { Auth0InitGate } from '@flintmint/web-shared/components/auth0-init-gate';
import { EmailVerification } from '@flintmint/web-shared/components/email-verification';

import { appRoutes } from '@/constants/routes';

import './assets/scss/index.scss';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// TEMPORARY design-system reference, registered only in the dev environment (deployed dev or a
// local dev server); elsewhere the path falls through to the library redirect like any unknown URL.
// Reads `import.meta.env` directly rather than through `ENVIRONMENT` so Vite folds the condition at
// build time and staging/production builds drop the lazy page chunk entirely.
// Inside ProtectedRoutes because several showcased components read the local DB, outside Layout so
// it renders without the app chrome.
const isDevEnvironment = import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'dev';

const componentLibraryRoutes = isDevEnvironment
  ? [
      {
        path: appRoutes.componentLibrary,
        lazy: async () => ({ Component: (await import('@/features/component-library')).ComponentLibrary }),
      },
    ]
  : [];

const router = sentryCreateBrowserRouter([
  {
    element: (
      <AppProvider>
        <Auth0InitGate accessDeniedPath={appRoutes.accessDenied} emailVerificationPath={appRoutes.emailVerification} />
      </AppProvider>
    ),
    children: [
      {
        path: appRoutes.login,
        element: <Login />,
      },
      {
        path: appRoutes.logout,
        element: <Logout />,
      },
      {
        path: appRoutes.noConnection,
        element: <NoConnection />,
      },
      {
        path: appRoutes.emailVerification,
        element: <EmailVerification logoutPath={appRoutes.logout} />,
      },
      {
        path: appRoutes.accessDenied,
        element: <AccessDenied logoutPath={appRoutes.logout} />,
      },
      {
        path: appRoutes.agreementEula,
        element: <AgreementPage agreement="eula" />,
      },
      {
        path: appRoutes.agreementBaa,
        element: <AgreementPage agreement="baa" />,
      },
      {
        path: '/',
        element: <ProtectedRoutes />,
        children: [
          ...componentLibraryRoutes,
          {
            element: <Layout />,
            children: [
              {
                index: true,
                element: <Navigate to={appRoutes.library} replace />,
              },
              {
                path: `${appRoutes.library}`,
                element: <DocumentWorkspace />,
                handle: {
                  leftSidebar: {
                    visible: true,
                    resizable: true,
                    collapsible: true,
                  },
                },
              },
              {
                path: `${appRoutes.library}/:id`,
                element: <DocumentWorkspace />,
                handle: {
                  leftSidebar: {
                    visible: true,
                    resizable: true,
                    collapsible: true,
                  },
                  rightSidebar: {
                    visible: true,
                    resizable: true,
                  },
                },
              },
              {
                path: `${appRoutes.cortex}`,
                element: <CortexWorkspace />,
                handle: {
                  leftSidebar: {
                    visible: true,
                    resizable: true,
                    collapsible: true,
                  },
                },
              },
              {
                path: `${appRoutes.cortex}/:id`,
                element: <CortexWorkspace />,
                handle: {
                  leftSidebar: {
                    visible: true,
                    resizable: true,
                    collapsible: true,
                  },
                },
              },
              {
                path: `${appRoutes.tags}/:tagId?`,
                element: <TagWorkspace />,
                handle: {
                  leftSidebar: {
                    visible: true,
                    resizable: false,
                    collapsible: true,
                  },
                },
              },
              {
                path: appRoutes.instructions,
                element: <ComingSoon slug="instructions" />,
                handle: {
                  leftSidebar: {
                    visible: true,
                    resizable: false,
                  },
                },
              },
              {
                path: appRoutes.archive,
                element: <Archive />,
              },
              {
                path: appRoutes.settings,
                element: <Outlet />,
                handle: {
                  leftSidebar: {
                    visible: true,
                    resizable: false,
                  },
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to={appRoutes.settingsPersonalization} replace />,
                  },
                  {
                    path: 'personalization',
                    element: <Personalization />,
                  },
                  {
                    path: 'billing',
                    element: <SubscriptionBilling />,
                  },
                  {
                    path: '*',
                    element: <Navigate to={appRoutes.settingsPersonalization} replace />,
                  },
                ],
              },
              {
                path: '*',
                element: <Navigate to={appRoutes.library} replace />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(<RouterProvider router={router} />);
